import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, X, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { WHATSAPP_URL, PHONE_DISPLAY } from "../data/content";

const GALLERY = [
  { src: "/assets/chef.jpg", caption: "Chef Naledi — the heart of Novaphi", tall: true },
  { src: "/assets/team.jpg", caption: "The Novaphi team", tall: true },
  { src: "/assets/products/brunch-box.jpg", caption: "The Novaphi brunch box" },
  { src: "/assets/products/grazing-two.jpg", caption: "Signature grazing board" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_ccbb2745-0ff1-4a1a-9bda-9ab07a0acb90/artifacts/iiy23y4f_IMG_8398.webp", caption: "Gold-standard buffet service" },
  { src: "/assets/products/braai-feast.jpg", caption: "Shisa nyama feast spread", tall: true },
  { src: "/assets/products/platter-savoury.jpg", caption: "Savoury finger food platter" },
  { src: "/assets/products/cupcakes.jpg", caption: "Celebration cupcakes" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_ccbb2745-0ff1-4a1a-9bda-9ab07a0acb90/artifacts/1bru8lpp_IMG_7854.webp", caption: "Candlelit buffet — private function", tall: true },
  { src: "/assets/products/grazing-celebration.jpg", caption: "Celebration grazing platter" },
  { src: "/assets/products/meal-prep-family.jpg", caption: "Weekly family meal prep" },
  { src: "/assets/products/muffin-blueberry.jpg", caption: "Blueberry muffins, boxed", tall: true },
  { src: "/assets/products/platter-sweet.jpg", caption: "Sweet treats platter" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_ccbb2745-0ff1-4a1a-9bda-9ab07a0acb90/artifacts/roik3nwg_IMG_0704.webp", caption: "The Novaphi braai board" },
  { src: "/assets/products/grazing-grand.jpg", caption: "Grand grazing collection" },
  { src: "/assets/products/gift-corporate.jpg", caption: "Corporate gifting, branded", tall: true },
  { src: "/assets/products/scone-classic.jpg", caption: "Classic scones" },
  { src: "/assets/products/platter-breakfast.jpg", caption: "Boardroom breakfast spread" },
  { src: "/assets/products/kombucha.jpg", caption: "Rooibos & mint kombucha" },
  { src: "/assets/products/sauce-boxed.jpg", caption: "Gift-boxed hot sweet chilli sauce" },
];

export default function Gallery() {
  const [active, setActive] = useState(null);

  const next = () => setActive((a) => (a + 1) % GALLERY.length);
  const prev = () => setActive((a) => (a - 1 + GALLERY.length) % GALLERY.length);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F9F9F6]" data-testid="gallery-page">
      <header className="border-b border-[#F9F9F6]/10 bg-[#0A0A0A]/95 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[70px] flex items-center justify-between">
          <a href="/" data-testid="gallery-back-home" className="flex items-center gap-3 text-[#F9F9F6]/70 hover:text-[#D4AF37] transition-colors text-xs uppercase tracking-[0.2em]">
            <ArrowLeft size={15} strokeWidth={1.5} /> Back to site
          </a>
          <img src="/assets/logo-white.png" alt="Novaphi Catering" className="h-12 w-auto" />
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="gallery-whatsapp-btn"
             className="flex items-center gap-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2 text-[10px] uppercase tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors">
            <MessageCircle size={13} strokeWidth={1.5} /> <span className="hidden sm:inline">{PHONE_DISPLAY}</span><span className="sm:hidden">WhatsApp</span>
          </a>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">The Gallery</p>
          <h1 className="font-serif-e text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight max-w-3xl">
            Every detail, on display.
          </h1>
          <p className="text-[#F9F9F6]/60 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
            Real Novaphi spreads, boards, boxes and bakes — tap any photo to view it full-screen.
          </p>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-14">
          {GALLERY.map((g, i) => (
            <motion.button key={g.src} onClick={() => setActive(i)} data-testid={`gallery-item-${i}`}
                           initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                           transition={{ duration: 0.6, delay: (i % 4) * 0.06 }}
                           className={`group relative overflow-hidden border border-[#F9F9F6]/10 hover:border-[#D4AF37]/50 transition-colors text-left ${g.tall ? "row-span-2 aspect-[4/7]" : "aspect-[4/5]"}`}>
              <img src={g.src} alt={g.caption} loading="lazy"
                   className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-4 pt-10 pb-3 text-[11px] uppercase tracking-[0.15em] text-[#F9F9F6]/90 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                {g.caption}
              </span>
            </motion.button>
          ))}
        </div>
      </main>

      <AnimatePresence>
        {active !== null && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4 md:p-10"
                      data-testid="gallery-lightbox" onClick={() => setActive(null)}>
            <button onClick={() => setActive(null)} data-testid="lightbox-close" aria-label="Close"
                    className="absolute top-5 right-5 text-[#F9F9F6]/70 hover:text-[#D4AF37] transition-colors z-10">
              <X size={28} strokeWidth={1.5} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" aria-label="Previous"
                    className="absolute left-3 md:left-8 text-[#F9F9F6]/70 hover:text-[#D4AF37] transition-colors z-10">
              <ChevronLeft size={36} strokeWidth={1} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" aria-label="Next"
                    className="absolute right-3 md:right-8 text-[#F9F9F6]/70 hover:text-[#D4AF37] transition-colors z-10">
              <ChevronRight size={36} strokeWidth={1} />
            </button>
            <motion.figure key={active} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                           transition={{ duration: 0.3 }} className="max-h-full" onClick={(e) => e.stopPropagation()}>
              <img src={GALLERY[active].src} alt={GALLERY[active].caption} data-testid="lightbox-image"
                   className="max-h-[80vh] max-w-full object-contain border border-[#D4AF37]/30" />
              <figcaption className="text-center text-[#F9F9F6]/70 text-sm mt-4 font-serif-e italic text-lg">
                {GALLERY[active].caption}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="border-t border-[#F9F9F6]/10 py-8 text-center">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#F9F9F6]/30">Novaphi Catering &amp; Events — Where every detail matters.</p>
      </footer>
    </div>
  );
}
